import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

import {
  IAccount,
  ICurrencyRates,
  IExhutAuthUser,
  IExhutProfile,
  IExpense2,
  IGroup2,
  IIncome,
  ILedger,
  ILimit,
  INotification,
  ISavings,
  ISavingsGoal,
  ISavingsGoalItem,
  ISubscription,
  ITotal,
  ITrip,
  IUser,
} from "../types/types";
import { EXHUT_WEB_LOCAL_STORAGE_TOKEN } from "../utils/constants";

export function useGetIncomeTotal(groupId?: string) {
  return _useGet<ITotal>(`groups/${groupId}/incomes/total`, [
    "groups",
    groupId,
    "incomes",
    "total",
  ]);
}

export function useGetIncomes(groupId?: string) {
  return _useGet<IIncome[]>(`groups/${groupId}/incomes`, [
    "groups",
    groupId,
    "incomes",
  ]);
}

export function useGetIncomesByid(groupId?: string, incomeId?: string) {
  return _useGet<IIncome[]>(`groups/${groupId}/incomes/${incomeId}`, [
    "groups",
    groupId,
    "incomes",
    incomeId,
  ]);
}

export function useGetNotifications() {
  return _useGet<INotification[]>(`transactions`, ["notifications"], true);
}

export function useGetSubscriptions() {
  return _useGet<ISubscription[]>(`subscriptions`, ["subscriptions"]);
}

export function useGetSubscriptionLimits() {
  return _useGet<ILimit>(`subscriptions/limits`, ["subscriptions", "limits"]);
}

export function useSearchUserByEmail() {
  const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);

  const headers: any = {
    "Content-Type": "application/json",
    Authorization: sign,
  };

  return useMutation<IExhutAuthUser[], Error, { email: string }>({
    mutationFn: async (req) => {
      return axios
        .get(
          `${process.env.REACT_APP_AUTH_BE_URL}/users/search?email=${req.email}`,
          {
            headers,
          },
        )
        .then((resp) => resp.data);
    },
    mutationKey: ["groups", "users"],
  });
}

export function useGetLedgerTransactionsTotal(groupId?: string, id?: string) {
  return _useGet<ITotal>(
    `groups/${groupId}/ledgers/${id}/transactions/total`,
    ["groups", groupId, "ledgers", id, "transactions", "total"],
    !!id && !!groupId,
  );
}

export function useGetLedgerTransactions(groupId?: string, ledgerId?: string) {
  return _useGet<IExpense2[]>(
    `groups/${groupId}/ledgers/${ledgerId}/transactions`,
    ["groups", groupId, "ledgers", ledgerId, "transactions"],
    !!ledgerId && !!groupId,
  );
}

export function useGetLedgerById(groupId?: string, id?: string) {
  return _useGet<ILedger>(
    `groups/${groupId}/ledgers/${id}`,
    ["groups", groupId, "ledgers", id],
    !!id && !!groupId,
  );
}

interface ISearchExpensesPayload {
  categoryFilter?: string;
  startDate: string;
  endDate: string;
  searchText?: string;
  miscOnly?: boolean;
  tripOnly?: boolean;
  excludeMisc?: boolean;
  excludeTrip?: boolean;
  subcategoryFilter?: string;
  groupId?: string;
}

export function useGetSearchExpenses2() {
  let headers: any = {
    "Content-Type": "application/json",
  };
  const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);
  headers["Authorization"] = sign;

  return useMutation({
    mutationFn: async (req: ISearchExpensesPayload) => {
      const params: string[] = [];
      if (req.categoryFilter) {
        params.push(`category=${req.categoryFilter}`);
      }

      if (req.subcategoryFilter) {
        params.push(`subcategory=${req.subcategoryFilter}`);
      }

      if (req.groupId) {
        params.push(`groupId=${req.groupId}`);
      }

      params.push(`tripOnly=${req.tripOnly ? "true" : "false"}`);
      params.push(`miscOnly=${req.miscOnly ? "true" : "false"}`);

      params.push(`excludeMisc=${req.excludeMisc ? "true" : "false"}`);
      params.push(`excludeTrip=${req.excludeTrip ? "true" : "false"}`);

      params.push(`startDate=${req.startDate}`);
      params.push(`endDate=${req.endDate}`);

      if (req.searchText) {
        params.push(`q=${req.searchText}`);
      }

      const queryParams = params.join("&");

      let _url = `${process.env.REACT_APP_BE_URL2}/groups/${req.groupId}/expenses/search?${queryParams}`;

      return axios
        .get(_url, {
          headers,
        })
        .then((resp) => resp.data);
    },
  });
}

export function useGetMiscExpenses(
  groupid?: string,
  startDate?: string,
  endDate?: string,
) {
  return _useGet<IExpense2[]>(
    `groups/${groupid}/expenses/misc?startDate=${startDate}&endDate=${endDate}`,
    ["expenses", "misc", groupid, startDate, endDate],
    !!groupid && !!startDate && !!endDate,
  );
}

export function useGetTripExpenses(groupid?: string, id?: string) {
  return _useGet<IExpense2[]>(
    `groups/${groupid}/trips/${id}/expenses`,
    ["groups", groupid, "trips", id, "expenses"],
    !!groupid && !!id,
  );
}

export function useGetTripById(groupid?: string, id?: string) {
  return _useGet<ITrip>(
    `groups/${groupid}/trips/${id}`,
    ["groups", groupid, "trips", id],
    !!groupid && !!id,
  );
}

export function useGetSavingsGoals(groupid?: string) {
  return _useGet<ISavingsGoal[]>(
    `groups/${groupid}/savings-goals`,
    ["groups", groupid, "savings-goals"],
    !!groupid,
  );
}

export function useGetSavingsGoalById(groupid?: string, id?: string) {
  return _useGet<ISavingsGoal>(
    `groups/${groupid}/savings-goals/${id}`,
    ["groups", groupid, "savings-goals", id],
    !!groupid && !!id,
  );
}

export function useGetSavingsGoalItems(groupid?: string, id?: string) {
  return _useGet<ISavingsGoalItem[]>(
    `groups/${groupid}/savings-goals/${id}/items`,
    ["groups", groupid, "savings-goals", id, "items"],
    !!groupid && !!id,
  );
}

export function useGetCurrencyRates(countryCode?: string) {
  return _useGet<ICurrencyRates>(
    `currency-rates?countryCode=${countryCode}`,
    ["currency-rates"],
    !!countryCode,
  );
}

export function useGetSavingsTotal2(groupid?: string) {
  return _useGet<ITotal>(
    `groups/${groupid}/savings/total`,
    ["groups", groupid!, "savings", "total"],
    !!groupid,
  );
}

export function useGetSavings(groupid?: string) {
  return _useGet<ISavings[]>(
    `groups/${groupid}/savings`,
    ["groups", groupid!, "savings"],
    !!groupid,
  );
}

export function useGetSavingsById(groupid?: string, id?: string) {
  return _useGet<ISavings>(
    `groups/${groupid}/savings/${id}`,
    ["groups", groupid!, "savings", id!],
    !!groupid && !!id,
  );
}

export function useGetExpenseById2(groupid?: string, expenseid?: string) {
  return _useGet<IExpense2>(
    `groups/${groupid}/expenses/${expenseid}`,
    ["expenses", expenseid],
    !!groupid && !!expenseid,
  );
}

interface IExpenseAnalysisByCategoryByMonth {
  month: number;
  monthName: string;
  total: number;
  year: number;
  data: [{ category: string; amount: number }];
}

export function useGetExpenseAnalysisByCategoryFor6Months2(groupid?: string) {
  return _useGet<IExpenseAnalysisByCategoryByMonth[]>(
    `groups/${groupid}/analysis/expenses/compare/category/previous/6`,
    [
      "groups",
      groupid,
      "analysis",
      "expenses",
      "compare",
      "category",
      "previous",
      "6months",
    ],
    !!groupid,
  );
}

interface IExpenseAnalysisBySubCategoryByMonth {
  month: number;
  monthName: string;
  total: number;
  year: number;
  data: [{ category: string; amount: number; subcategory: string }];
}

export function useGetExpenseAnalysisBySubCategory2For6Months(
  groupid?: string,
) {
  return _useGet<IExpenseAnalysisBySubCategoryByMonth[]>(
    `groups/${groupid}/analysis/expenses/compare/subcategory/previous/6`,
    [
      "groups",
      groupid,
      "analysis",
      "expenses",
      "compare",
      "subcategory",
      "previous",
      "6months",
    ],
    !!groupid,
  );
}

interface IExpensesBySubCategoryDiffData {
  category: string;
  prevMonthTotal: number;
  subcategory: string;
  thisMonthTotal: number;
}

interface IExpensesBySubCategoryDiffResponse {
  data: IExpensesBySubCategoryDiffData[];
  prevMonth: number;
  prevMonthName: string;
  prevMonthYear: number;
  thisMonth: number;
  thisMonthName: string;
  thisYear: string;
}

export function useGetExpensesBySubCategoryDiff2(
  groupid?: string,
  date?: string,
) {
  return _useGet<IExpensesBySubCategoryDiffResponse>(
    `groups/${groupid}/analysis/expenses/compare/subcategory?date=${date}`,
    ["groups", groupid, "analysis", "expenses", "compare", "subcategory", date],
    !!groupid && !!date,
  );
}

interface IExpensesByCategory {
  category: string;
  amount: number;
}
export function useGetExpensesByCategory2(
  groupid?: string,
  startDate?: string,
  endDate?: string,
) {
  return _useGet<IExpensesByCategory[]>(
    `groups/${groupid}/analysis/expenses/total/category?startDate=${startDate}&endDate=${endDate}`,
    [
      "groups",
      groupid,
      "analysis",
      "expenses",
      "total",
      "category",
      `${startDate}`,
      `${endDate}`,
    ],
    !!groupid && !!startDate && !!endDate,
  );
}

interface IExpenseTillDate {
  total: number;
  month: number;
  monthName: string;
  year: number;
}

export function useGetExpensesTillDate(
  groupid?: string,
  prevMonths: number = 3,
) {
  return _useGet<IExpenseTillDate[]>(
    `groups/${groupid}/analysis/expenses/tillDate?prevMonths=${prevMonths}`,
    ["groups", groupid, "expenses-tilldate"],
    !!groupid,
  );
}

export function useGetMiscExpensesTotal(
  groupid?: string,
  startDate?: string,
  endDate?: string,
) {
  return _useGet<ITotal>(
    `groups/${groupid}/expenses/misc/total?startDate=${startDate}&endDate=${endDate}`,
    ["expenses", "misc", "total", startDate, endDate],
    !!groupid && !!startDate && !!endDate,
  );
}

export function useGetExpensesTotal2(
  groupid: string,
  startDate: string,
  endDate: string,
) {
  return _useGet<ITotal>(
    `groups/${groupid}/expenses/total?startDate=${startDate}&endDate=${endDate}`,
    ["expensesTotal", groupid, `${startDate}`, `${endDate}`],
    !!groupid,
  );
}

export function useGetExpensesForMonth2(
  groupid: string,
  startDate: string,
  endDate: string,
  category?: string,
  subcategory?: string,
) {
  const extraParams: string[] = [];

  extraParams.push(`startDate=${startDate}`);
  extraParams.push(`endDate=${endDate}`);

  if (category) {
    extraParams.push(`cat=${encodeURIComponent(category)}`);
  }
  if (subcategory) {
    extraParams.push(`subcat=${encodeURIComponent(subcategory)}`);
  }
  const extraQuery = extraParams.length > 0 ? `?${extraParams.join("&")}` : "";
  return _useGet<IExpense2[]>(
    `groups/${groupid}/expenses${extraQuery}`,
    ["groups", groupid, `expenses`, startDate, endDate, category],
    !!groupid,
  );
}

export function useGetGroups2() {
  return _useGet<IGroup2[]>("groups", ["groups2"], true);
}

export function useGetGroupById2(groupid?: string) {
  return _useGet<IGroup2>(`groups/${groupid}`, ["groups2", groupid], !!groupid);
}

export function useGetGroupUsers2(groupid?: string) {
  return _useGet<IExhutAuthUser[]>(
    `groups/${groupid}/users`,
    ["groups", groupid, "users"],
    !!groupid,
  );
}

export function useGetAllLedgers(groupId?: string) {
  return _useGet<ILedger[]>(
    `groups/${groupId}/ledgers`,
    ["groups", groupId, "ledgers"],
    !!groupId,
  );
}

export function useGetAccounts() {
  return _useGet<IAccount[]>(`accounts`, ["accounts"], true);
}

export function useGetAccountById(id?: string, enabled?: boolean) {
  return _useGet<IAccount>(`accounts/${id}`, ["accounts", id], !!enabled);
}

export function useGetTripsInMonth(
  groupid: string,
  startDate: string,
  endDate: string,
) {
  return _useGet<ITrip[]>(
    `groups/${groupid}/trips?startDate=${startDate}&endDate=${endDate}`,
    ["groups", groupid, "trips", `${startDate}`, `${endDate}`],
    !!groupid && !!startDate && !!endDate,
  );
}

export function useGetAllTrips(groupid?: string) {
  return _useGet<ITrip[]>(
    `groups/${groupid}/trips`,
    ["groups", groupid, "trips"],
    !!groupid,
  );
}

export function useGetCategories() {
  return _useGet<Record<string, string[]>>(`category`, ["category"], true);
}
export function useGetCategoriesOnly() {
  return _useGet<string[]>(`category/only`, ["category-only"], true);
}

export function useGetSession(enabled: boolean) {
  return _useGetAuthBE<IUser>("session", ["session"], enabled);
}

export function useGetMyUserDetailsFromAuthBESession() {
  return _useGetAuthBE<IExhutProfile>(
    "users/profile",
    ["users", "profile"],
    true,
  );
}

function _useGetAuthBE<T1>(
  path: string,
  keys: (string | undefined | null)[],
  enabled?: boolean,
  authenticated: boolean = true,
) {
  return _useGet<T1>(
    path,
    keys,
    enabled,
    authenticated,
    process.env.REACT_APP_AUTH_BE_URL,
  );
}

// uses new backend service
function _useGet<T1>(
  path: string,
  keys: (string | undefined | null)[],
  enabled?: boolean,
  authenticated: boolean = true,
  host?: string,
) {
  let headers: any = {};

  const hostUrl = host ?? process.env.REACT_APP_BE_URL2;

  if (authenticated) {
    const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);
    headers["Authorization"] = sign;
  }

  return useQuery<T1, Error>({
    queryFn: async () => {
      return axios
        .get(`${hostUrl}/${path}`, {
          headers,
        })
        .then((resp) => resp.data);
    },
    queryKey: keys,
    enabled,
    retry: 0,
  });
}
